// src/pages/SummaryPage.jsx
import React, { useEffect, useState } from 'react';
import axios from '../api/axios';
import GroupDetailModal from './modals/GroupDetailModal';
import LetterConfirmModal from './modals/LetterConfirmModal';
import LetterViewModal from './modals/LetterViewModal';

const API = 'https://expertise.gov.kz/api';

export default function SummaryPage({ locale }) {
  const [groups, setGroups] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [letterViewGroup, setLetterViewGroup] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [tempOutgoingNumbers, setTempOutgoingNumbers] = useState({});
  const [loadingGroupId, setLoadingGroupId] = useState(null);
  const [letterConfirmGroup, setLetterConfirmGroup] = useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  // ============================================================
  //  Деректерді жүктеу
  // ============================================================

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API}/gov/application-groups`, {
        headers: authHeaders,
      });
      setGroups(res.data.data || []);
    } catch {
      setError(locale === 'kz' ? 'Қате орын алды' : 'Произошла ошибка');
    }
  };

  useEffect(() => {
    axios
      .get('/internal/profile', { headers: authHeaders })
      .then((res) => setProfile(res.data))
      .catch(() => setProfile(null));
  }, []);

  useEffect(() => {
    setLoading(true);
    setLetterConfirmGroup(null);
    setLetterViewGroup(null);
    setSelectedApplication(null);

    fetchGroups().finally(() => setLoading(false));
  }, [locale]);

  // ============================================================
  //  Исходящий нөмірін сақтау
  // ============================================================

  const handleInputChange = (groupId, value) => {
    setTempOutgoingNumbers((prev) => ({ ...prev, [groupId]: value }));
  };

  const handleSetOutgoingNumber = async (groupId, outgoing_number) => {
    await axios.post(
      `${API}/application-groups/${groupId}/set-outgoing-number`,
      { outgoing_number },
      { headers: authHeaders }
    );
  };

  // ============================================================
  //  Хат жасау (store API)
  // ============================================================

  const handleSendLetter = async (groupId, theme, description) => {
    try {
      const group = groups.find((g) => g.id === groupId);
      if (!group) return;

      // Нөмір сақталмаған болса — алдымен сақтаймыз
      if (!group.outgoing_number) {
        const number = tempOutgoingNumbers[group.id];
        if (!number) {
          alert(locale === 'kz' ? 'Нөмірді енгізіңіз' : 'Введите номер');
          return;
        }
        await handleSetOutgoingNumber(group.id, number);
      }

      // ✅ POST /letters — хат жасау + PDF генерация
      await axios.post(
        `${API}/letters`,
        { application_group_id: groupId, theme, description },
        { headers: authHeaders }
      );

      alert(
        locale === 'kz'
          ? '✅ Хат сәтті жасалды, PDF дайын!'
          : '✅ Письмо создано, PDF готов!'
      );

      setLetterConfirmGroup(null);
      await fetchGroups();
    } catch (err) {

      const msg = err.response?.data?.message || '';
      alert(
        locale === 'kz'
          ? `❌ Қате: ${msg || 'Хат жасалмады'}`
          : `❌ Ошибка: ${msg || 'Не удалось создать письмо'}`
      );
    }
  };

  // ============================================================
  //  ЭЦП арқылы қол қою — SummaryPage деңгейінде
  //  (LetterViewModal өзі handleSign жасайды, бірақ
  //   SummaryPage-тен де prop ретінде береміз)
  // ============================================================

  const handleSignLetterWithEcp = async (letter) => {
    if (!window.NCA || typeof window.NCA.createCMSSignatureFromBase64 !== 'function') {
      alert(
        locale === 'kz'
          ? '❌ NCALayer табылмады. Бағдарламаны іске қосыңыз.'
          : '❌ NCALayer не найден. Запустите программу.'
      );
      return;
    }

    try {
      // 1) NCALayer-ге қосылу
      const connected = await window.NCA.connect();
      if (!connected) {
        alert(
          locale === 'kz'
            ? '❌ NCALayer іске қосылмаған'
            : '❌ NCALayer не запущен'
        );
        return;
      }

      // 2) PDF base64 алу
      const res = await axios.get(`${API}/letters/${letter.id}/base64`, {
        headers: authHeaders,
      });

      const base64 = res.data?.base64;
      if (!base64) {
        alert(locale === 'kz' ? '❌ PDF табылмады' : '❌ PDF не найден');
        return;
      }

      // 3) CMS қол қою
      window.NCA.createCMSSignatureFromBase64(
        'PKCS12',
        'SIGNATURE',
        base64,
        true,
        async (result) => {
          const code = result?.getCode ? result.getCode() : result?.code;

          if (String(code) !== '200') {
            if (String(code) !== '100') {
              alert(
                locale === 'kz'
                  ? '❌ Қол қою сәтсіз аяқталды'
                  : '❌ Подпись не выполнена'
              );
            }
            return;
          }

          const signedCms = result?.getResponseObject
            ? result.getResponseObject()
            : result?.responseObject;

          // 4) Серверге жіберу
          try {
            await axios.post(
              `${API}/letters/${letter.id}/sign`,
              { signed_cms_base64: signedCms },
              { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
            );

            alert(
              locale === 'kz'
                ? '✅ Хатқа сәтті қол қойылды!'
                : '✅ Письмо успешно подписано!'
            );

            setLetterViewGroup(null);
            await fetchGroups();
          } catch (err) {
            const msg = err?.response?.data?.message || err.message;
            alert(`❌ ${msg}`);
          }
        }
      );
    } catch (err) {

      alert(
        locale === 'kz'
          ? '❌ Жүйелік қате орын алды'
          : '❌ Системная ошибка'
      );
    }
  };

  // ============================================================
  //  Render
  // ============================================================

  if (loading)
    return <p>{locale === 'kz' ? 'Жүктелуде...' : 'Загрузка...'}</p>;
  if (error)
    return <p style={{ color: 'red' }}>{error}</p>;

  const isGos =
    profile?.username === 'gos' || profile?.department_id === 6;

  return (
    <section className="dashboard-body">
      <h2>
        📑{' '}
        {locale === 'kz'
          ? 'Мемлекеттік сервиске жіберілген топтар'
          : 'Отправленные в гос сервис группы'}
      </h2>

      {groups.length === 0 ? (
        <p>{locale === 'kz' ? 'Группалар жоқ' : 'Нет групп'}</p>
      ) : (
        groups.map((group) => (
          <div
            key={group.id}
            className="group-card"
            style={{
              border: '1px solid #ccc',
              padding: '1rem',
              marginBottom: '2rem',
              borderRadius: '8px',
              backgroundColor: '#f9f9f9',
            }}
          >
            <h3>{group.title}</h3>
            <p>
              <strong>
                {locale === 'kz' ? 'Құрылған уақыты' : 'Создано'}:
              </strong>{' '}
              {new Date(group.created_at).toLocaleString()}
            </p>

            {/* Өтінімдер кестесі */}
            <div style={{ marginTop: '1.5rem' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  backgroundColor: '#fff',
                  border: '1px solid #d1d5db',
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '14px',
                  color: '#111827',
                }}
              >
                <thead style={{ backgroundColor: '#f3f4f6' }}>
                  <tr>
                    <th style={{ padding: '10px', border: '1px solid #d1d5db', textAlign: 'left' }}>
                      {locale === 'kz' ? 'Санат' : 'Категория'}
                    </th>
                    <th style={{ padding: '10px', border: '1px solid #d1d5db', textAlign: 'left' }}>
                      {locale === 'kz' ? 'Беттер' : 'Страниц'}
                    </th>
                    <th style={{ padding: '10px', border: '1px solid #d1d5db', textAlign: 'left' }}>
                      {locale === 'kz' ? 'Авторлар' : 'Авторы'}
                    </th>
                    <th style={{ padding: '10px', border: '1px solid #d1d5db', textAlign: 'left' }}>
                      {locale === 'kz' ? 'Қорытынды' : 'Итог'}
                    </th>
                    <th style={{ padding: '10px', border: '1px solid #d1d5db', textAlign: 'center' }}>
                      {locale === 'kz' ? 'Әрекет' : 'Действие'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(group.applications || []).map((app) => (
                    <tr key={app.id} style={{ backgroundColor: '#ffffff' }}>
                      <td style={{ padding: '10px', border: '1px solid #e5e7eb' }}>
                        {locale === 'kz'
                          ? app.category?.name_kz
                          : app.category?.name_ru}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #e5e7eb' }}>
                        {app.page_count ?? '—'}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #e5e7eb' }}>
                        {app.authors ?? '—'}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #e5e7eb' }}>
                        {app.final_document?.average_score != null
                          ? app.final_document.average_score
                          : '—'}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                        <button
                          onClick={() => setSelectedApplication(app)}
                          style={{
                            padding: '6px 14px',
                            backgroundColor: '#1d4ed8',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                          }}
                        >
                          {locale === 'kz' ? 'Көру' : 'Посмотреть'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Хат блогы */}
            <div
              style={{
                marginTop: '1rem',
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {group.outgoing_number ? (
                <>
                  <span style={{ fontWeight: 'bold' }}>
                    📎 {locale === 'kz' ? 'Нөмір:' : 'Номер:'}{' '}
                    {group.outgoing_number}
                  </span>

                  <button
                    onClick={() => setLetterViewGroup(group)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      backgroundColor: '#17a2b8',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    {locale === 'kz' ? '📬 Хатты қарау' : '📬 Посмотреть письма'}
                  </button>

                  {/* Хат жоқ болса — жасау кнопкасы */}
                  {isGos && (!group.letters || group.letters.length === 0) && (
                    <button
                      onClick={() => setLetterConfirmGroup(group)}
                      style={{
                        padding: '0.4rem 0.8rem',
                        backgroundColor: '#28a745',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      {locale === 'kz' ? '➕ Хат жасау' : '➕ Создать письмо'}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {isGos ? (
                    <>
                      <input
                        type="text"
                        placeholder={locale === 'kz' ? 'Нөмір' : 'Номер'}
                        value={tempOutgoingNumbers[group.id] || ''}
                        onChange={(e) => handleInputChange(group.id, e.target.value)}
                        style={{
                          padding: '0.3rem 0.6rem',
                          width: '130px',
                          fontSize: '0.9rem',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                        }}
                      />

                      <button
                        onClick={async () => {
                          const number = tempOutgoingNumbers[group.id];
                          if (!number) {
                            return alert(
                              locale === 'kz'
                                ? 'Нөмірді енгізіңіз'
                                : 'Введите номер'
                            );
                          }
                          setLoadingGroupId(group.id);
                          try {
                            await handleSetOutgoingNumber(group.id, number);
                            // Нөмір сақталды — хат жасау модалкасын ашу
                            const updated = { ...group, outgoing_number: number };
                            setLetterConfirmGroup(updated);
                            await fetchGroups();
                          } catch (err) {
                          
                            alert(
                              locale === 'kz'
                                ? '❌ Нөмірді сақтау қатесі'
                                : '❌ Ошибка при сохранении номера'
                            );
                          } finally {
                            setLoadingGroupId(null);
                          }
                        }}
                        disabled={loadingGroupId === group.id}
                        style={{
                          padding: '0.4rem 1rem',
                          backgroundColor:
                            loadingGroupId === group.id ? '#aaa' : '#28a745',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor:
                            loadingGroupId === group.id
                              ? 'not-allowed'
                              : 'pointer',
                        }}
                      >
                        {loadingGroupId === group.id
                          ? locale === 'kz'
                            ? 'Сақталуда...'
                            : 'Сохранение...'
                          : locale === 'kz'
                          ? '📄 Хат құру'
                          : '📄 Создать письмо'}
                      </button>
                    </>
                  ) : (
                    <p style={{ color: 'crimson', fontWeight: '500' }}>
                      {locale === 'kz'
                        ? 'Сараптаманы жоспарлау бөлімі хат нөмірін енгізуі тиіс'
                        : 'Отдел планирования экспертизы должен ввести номер письма'}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        ))
      )}

      {/* Өтінімді қарау модалкасы */}
      {selectedApplication && (
        <GroupDetailModal
          application={selectedApplication}
          onClose={() => setSelectedApplication(null)}
          locale={locale}
        />
      )}

      {/* Хат жасау модалкасы */}
      {letterConfirmGroup && (
        <LetterConfirmModal
          group={letterConfirmGroup}
          onCancel={() => setLetterConfirmGroup(null)}
          onConfirm={handleSendLetter}
          locale={locale}
        />
      )}

      {/* Хаттар тізімі модалкасы */}
      {letterViewGroup && (
        <LetterViewModal
          letters={letterViewGroup.letters || []}
          locale={locale}
          onClose={() => setLetterViewGroup(null)}
          onSign={handleSignLetterWithEcp}
          profile={profile}
        />
      )}
    </section>
  );
}